/**
 * 语文听写（家长端 · 孩子下面的第三步页）：**听写设置 / 布置听写 / 做听写**。
 *
 * ## 为什么这一页在**孩子下面**（`/students/:id/dictation`），
 * ## 而数据录入在顶层工具页（`/tools/dictation`）
 *
 * 用户原话："针对每一个小孩的听写设置、听写布置以及听写任务的布置，
 * 就可以放在某个具体的孩子下面。"
 *
 * 于是两件事分得很清楚（这也是它们**路径不同**的原因）：
 *
 * | 界面 | 路径 | 数据 | 为什么 |
 * | --- | --- | --- | --- |
 * | 数据录入（课本/课文/生字/词语） | `/tools/dictation` | 全家共用（表里没有 `student_id`） | 挂到某个孩子下面会让人以为是"这个孩子专属的字表" |
 * | 听写设置 / 布置 / 做听写 / 历史 | `/students/:id/dictation` | **这个孩子的**（会话表有 `student_id`） | 一次听写是"给谁听"，本来就是孩子的事 |
 *
 * ⚠️ 上一轮这里是**空的**（`StudentDetailPage` 里留了一段注释说明"本轮不留一个
 * 点了没用的按钮"）。这一步把它填上了：入口是孩子详情页那一排按钮里的
 * **「语文听写」**。
 *
 * ## 这一页做四件事
 *
 * 1. **布置听写** —— 选词条件（年级/学期/类别/随机几个）→ 节奏 → 创建会话。
 *    取词由**服务端**用第一步的 `pickDictationWords()` 完成（前端不重写取词）。
 * 2. **做听写** —— 进入播放器（`SessionPlayPage`）：按节奏读 N 遍 + 间隔，
 *    可暂停/重播/跳过。**必须有「开始播放」按钮**（浏览器自动播放限制，见播放器文件头）。
 * 3. **看历史 / 回看** —— 只读，不做批改（批改是第四步）。
 * 4. **朗读录音** —— 给多音字录一段家长的声音覆盖 TTS。这是**唯一**能纠正读音的路：
 *    `speechSynthesis` 只吃文字，读不了拼音。
 *
 * ## 三态（本项目踩过四次的那条线）
 *
 * 这一页的规矩与其他听写页一致：
 *   · `total` 拿不到 → 「—」；真 0 → 「0」；
 *   · 列表字段拿不到（解码成 `null`）→ 明说"这份数据没拿到"，**不是**"一条都没有"；
 *   · 查询失败 → 显示后端的中文 message 与重试按钮，**不吞成空**。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/Bits';
import { useToast } from '../../components/ui/Toast';
import { toErrorMessage } from '../../lib/api/error';
import { useStudent } from '../../lib/queries';
import {
  IconAlertTriangle,
  IconBookOpen,
  IconHistory,
  IconMic,
  IconPlus,
  IconTrash,
} from '../../components/ui/Icons';
import { countLabel, semesterLabel } from '../../lib/api/dictation';
import * as sessionApi from '../../lib/api/dictationSessions';
import {
  buildPickPayload,
  describeAppliedCriteria,
  draftFromCriteria,
  initialBuildDraft,
  parseRandomCount,
  validateBuildDraft,
  type BuildDraft,
} from '../../lib/dictationBuild';
import { TempoForm } from '../../components/dictation/DictationTempoForm';
import { DictationRecorderPanel } from '../../components/dictation/DictationRecorderPanel';
import { DictationAssignmentsPanel } from '../../components/dictation/DictationAssignmentsPanel';
import { AssignmentAdminActions } from '../../components/dictation/AssignmentAdminActions';
import * as assignmentApi from '../../lib/api/dictationAssignments';
import {
  batchLabel,
  describeGroupProgress,
  groupHistoryByAssignment,
} from '../../lib/dictationHistory';
import {
  loadOpenGroups,
  saveOpenGroups,
  toggleGroupKey,
} from '../../lib/dictationHistoryOpen';
import { GRADE_CODES, GRADES, gradeLabel, type DictationTempoConfig } from '@fht/shared';

/**
 * 四个页签。
 *
 * `assignments`（批量任务，迁移 0020）与 `build`（布置一次听写）是**两件事**：
 *   · `build` —— 出**一次** 10 个词，今天做完就完了；
 *   · `assignments` —— 立**一个长期任务**（范围 + 每次几个），孩子一批一批往下听。
 * 用户的需求是后者（"一次性布置一本书或者几本书……他可以分次去完成"）。
 */
type Tab = 'build' | 'assignments' | 'history' | 'audios';

/** 查询键：全部挂在这个孩子下面（换孩子就重新取）。 */
const sKeys = {
  root: (studentId: string) => ['dictation-sessions', studentId] as const,
  tempo: (studentId: string) => ['dictation-sessions', studentId, 'tempo'] as const,
  list: (studentId: string, status: string) =>
    ['dictation-sessions', studentId, 'list', status] as const,
  audios: (studentId: string) => ['dictation-sessions', studentId, 'audios'] as const,
};

/**
 * 稳定的空数组常量。
 *
 * ⚠️ 不能写 `?? []`：字面量每次渲染都是新引用，会让下面 `useMemo` 的分组
 * 每帧重算一遍（数据没变也算）。同一个坑在别处也踩过（`dictationBuild` 的说明）。
 */
const EMPTY_ASSIGNMENTS: assignmentApi.AssignmentView[] = [];

export function ParentStudentDictationPage() {
  const { id = '' } = useParams<{ id: string }>();
  /**
   * `?recording=1` = 从"_某个会话里点了『给这个词录音』"跳过来，
   * 直接落到录音页签、并把这个词预选上。**不新开路由**：录音是这一页的一段，
   * 多一条路由只会多一个返回链要维护。
   */
  const [searchParams] = useSearchParams();
  const focusWordId = searchParams.get('wordId');
  const focusWordText = searchParams.get('wordText');
  /**
   * 初始页签（优先级从上到下）：
   *   · `?recording=1` → 录音页签（从"给这个词录音"跳进来）；
   *   · `?from=<会话 id>` → 「布置听写」（"照样再来一次"要把条件填回那张表单）；
   *   · `?tab=history` → 听写历史（**批改页的「回听写历史」按钮**用它 ——
   *     用户要求"返回到听写历史"，落到默认的「批量任务」页签就不算回去了）；
   *   · 其余 → **批量任务**（用户最常用的入口：一本书分几次听）。
   *
   * ⚠️ 只在**首次挂载**时读一次：之后家长自己点页签不该被 URL 拽回去
   *    （`setTab` 也不会改 URL，两者不会打架）。
   */
  const [tab, setTab] = useState<Tab>(() => {
    if (searchParams.get('recording') === '1') return 'audios';
    if ((searchParams.get('from') ?? '') !== '') return 'build';
    const requested = searchParams.get('tab');
    if (requested === 'history' || requested === 'build' || requested === 'audios') return requested;
    return 'assignments';
  });
  const toast = useToast();
  const qc = useQueryClient();

  const studentQuery = useStudent(id);
  const student = studentQuery.data ?? null;
  const canEdit = student?.permission === 'owner' || student?.permission === 'editor';

  // ---------------------------------------------------------------- 布置听写
  const [draft, setDraft] = useState<BuildDraft>(() => initialBuildDraft());
  const [createError, setCreateError] = useState<string | null>(null);

  const tempoQuery = useQuery({
    queryKey: sKeys.tempo(id),
    queryFn: () => sessionApi.fetchTempoSuggestion(id),
    enabled: id !== '',
  });

  /**
   * 「照样再来一次」（`?from=<会话 id>`）：把**上一次的选词条件与节奏**填回表单。
   *
   * ⚠️ 只填条件、**不照抄词**（理由见 `draftFromCriteria` 的注释：家长要的是
   *    "同样的范围、再来一组"，不是"考同一张卷子"）。
   *
   * ⚠️ 2026-10-29 修：这一条以前从不切页签。家长在「听写历史」里点它时，
   *    表单确实被填好了，但**当前还停在历史页签**，看起来就像"点了没反应"。
   *    现在填完顺手切到「布置听写」，并记住"已经为哪个 session 填过"
   *    （用 ref 而不是布尔 state：换一条 `?from=` 时要能再填一次）。
   */
  const fromSessionId = searchParams.get('from');
  const fromQuery = useQuery({
    queryKey: ['dictation-sessions', id, 'detail', fromSessionId ?? ''] as const,
    queryFn: () => sessionApi.getSession(id, fromSessionId ?? ''),
    enabled: id !== '' && fromSessionId !== null && fromSessionId !== '',
  });
  const fromAppliedFor = useRef<string | null>(null);
  useEffect(() => {
    if (fromSessionId === null || fromSessionId === '') return;
    if (fromAppliedFor.current === fromSessionId) return;
    const source = fromQuery.data;
    if (source === undefined) return;
    fromAppliedFor.current = fromSessionId;
    setDraft((prev) => draftFromCriteria(source.pickCriteria, { ...prev, tempo: source.tempo }));
    setTab('build');
  }, [fromSessionId, fromQuery.data]);

  /**
   * 用"上次的节奏"预填一次。
   *
   * ⚠️ 只在**家长还没动过**节奏时填（`draft.tempoTouched === false`），否则会把
   *    家长刚调好的数覆盖掉 —— 那是本项目最招人烦的一类 bug。
   */
  const tempoSuggestion = tempoQuery.data;
  const effectiveTempo: DictationTempoConfig = draft.tempoTouched
    ? draft.tempo
    : (tempoSuggestion?.tempo ?? draft.tempo);

  const createMutation = useMutation({
    mutationFn: async () => {
      const validation = validateBuildDraft({ ...draft, tempo: effectiveTempo });
      if (!validation.ok) throw new Error(validation.message);
      return sessionApi.createSession(id, {
        pick: buildPickPayload({ ...draft, tempo: effectiveTempo }),
        title: draft.title.trim(),
        tempo: effectiveTempo,
      });
    },
    onSuccess: async (session) => {
      toast.showToast(`已布置「${session.title}」，共 ${session.questionCount} 个词`, 'success');
      await qc.invalidateQueries({ queryKey: sKeys.root(id) });
      /**
       * 布置完**不自动跳进播放器**：家长通常是在孩子坐下之前先把听写摆好，
       * 而播放器一进去就要点「开始播放」才出声 —— 自动跳过去会让他以为已经开始了。
       * 列表里那条「开始听写」才是入口。
       *
       * ⚠️ 2026-10-29 改：**不再自动展开**。用户原话："默认任务要折叠呀……
       *    跟桌面端那样。" 之前这里会展开刚建的那一组，并把它写进 sessionStorage，
       *    于是手机上从此一直是展开的。现在只切到「听写历史」，组头（`N 批 · 进度`）
       *    足够让家长确认"刚建的在不在"。
       */
      setTab('history');
    },
    onError: (err: unknown) => setCreateError(toErrorMessage(err)),
  });

  // ---------------------------------------------------------------- 历史会话
  const [historyStatus, setHistoryStatus] = useState('');
  const historyQuery = useQuery({
    queryKey: sKeys.list(id, historyStatus),
    queryFn: () => sessionApi.listSessions(id, { limit: 20, status: historyStatus || undefined }),
    enabled: id !== '' && tab === 'history',
  });

  const sessions = historyQuery.data?.sessions ?? null;
  const sessionsMissing = historyQuery.data !== undefined && sessions === null;

  /**
   * 任务清单（只用来给历史**分组**：每一批挂回它属于的那个任务）。
   *
   * 用户原话："每一批听写的历史，都挂在这个总的听写任务下面。不然以后听写历史
   * 一多，多少批、多少次，都不知道哪一批属于哪个任务。"
   *
   * ⚠️ 与「批量任务」页签共用同一个 query key，所以两处不会各取一遍。
   */
  const historyAssignmentsQuery = useQuery({
    queryKey: ['dictation-assignments', id, 'list'] as const,
    queryFn: () => assignmentApi.listAssignments(id, { limit: 50 }),
    enabled: id !== '' && tab === 'history',
  });
  const historyAssignments = historyAssignmentsQuery.data?.assignments ?? EMPTY_ASSIGNMENTS;
  const historyGroups = useMemo(
    () =>
      groupHistoryByAssignment(sessions ?? [], historyAssignments, {
        /**
         * ⚠️ 按状态筛选时**不补空任务组**：筛选"待开始"却冒出一堆"0 批"的任务，
         *    家长会以为筛选没生效（见 `GroupHistoryOptions` 的注释）。
         */
        includeEmptyTasks: historyStatus === '',
      }),
    [sessions, historyAssignments, historyStatus],
  );

  /**
   * 哪些任务组是**展开**的 —— **跨页面记住**（2026-10-29 第三版）。
   *
   * 用户原话：
   *   · 第一版要求："默认情况下……所有的批次听写历史都要折叠起来。"
   *   · 第二版补充："点进去之后，再点击「回听写历史」，回去之后应该保持原来的展开状态……
   *     只有第一次点进「听写历史」的时候，才需要默认是收起状态。"
   *
   * 所以状态存在 `sessionStorage`（见 `lib/dictationHistoryOpen.ts`）：
   * 点进某批（开始听写 / 看题目 / 再听一遍 / 去批改）再回来，展开的组还在；
   * 而这一趟浏览里**第一次**进来时读不到任何东西 → 全部收起，正是要的默认值。
   *
   * ⚠️ 用"展开集合"而不是"折叠集合"：新出现的任务默认是折叠的。
   */
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => loadOpenGroups(id));
  /**
   * 换孩子时重新读（路由参数变了，组件不会重挂，内存里还是上一个孩子的集合）。
   * `savedFor` 记着"当前这份集合属于哪个孩子"，避免把上个孩子的集合写进新孩子的键。
   */
  const openGroupsFor = useRef(id);
  useEffect(() => {
    if (openGroupsFor.current !== id) {
      openGroupsFor.current = id;
      setExpandedGroups(loadOpenGroups(id));
      return;
    }
    saveOpenGroups(id, expandedGroups);
  }, [id, expandedGroups]);

  const toggleGroup = (key: string) => setExpandedGroups((prev) => toggleGroupKey(prev, key));

  // ---------------------------------------------------------------- 朗读录音
  const audiosQuery = useQuery({
    queryKey: sKeys.audios(id),
    queryFn: () => sessionApi.listAudios(id, { limit: 50 }),
    enabled: id !== '' && tab === 'audios',
  });

  const loadingStudent = studentQuery.isLoading;

  /**
   * 删「独立听写」的一次会话（题目随外键一起删）。
   *
   * ⚠️ 只给 `assignmentId === null` 的行渲染按钮：批量任务的批由组头统一管理
   *    （`AssignmentAdminActions` 的「删除」），单批删除会把题目一起删掉，
   *    在历史里很容易误删。
   */
  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => sessionApi.deleteSession(id, sessionId),
    onSuccess: async () => {
      toast.showToast('已删除这次听写', 'success');
      await qc.invalidateQueries({ queryKey: sKeys.root(id) });
    },
    onError: (err: unknown) => toast.showToast(`删除失败：${toErrorMessage(err)}`, 'error'),
  });

  const tabs: { key: Tab; label: string; hint: string }[] = [
    { key: 'assignments', label: '批量任务', hint: '一本书 · 每次几个' },
    { key: 'build', label: '布置听写', hint: '选词 + 节奏' },
    { key: 'history', label: '听写历史', hint: '开始听写 / 回看' },
    { key: 'audios', label: '朗读录音', hint: '替换读错音的词' },
  ];

  // ---------------------------------------------------------------- 渲染
  if (loadingStudent) {
    return (
      <AppShell kind="parent" title="语文听写">
        <div className="space-y-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (studentQuery.isError || student === null) {
    return (
      <AppShell kind="parent" title="语文听写">
        <ErrorState
          message={`没拿到这个孩子的信息：${toErrorMessage(studentQuery.error)}`}
          onRetry={() => void studentQuery.refetch()}
        />
      </AppShell>
    );
  }

  /** 默认年级：这个孩子自己的年级（"今天听写三年级上册"就是家长的心智模型）。 */
  const defaultGrades = student.gradeCode === '' ? [...GRADE_CODES] : [student.gradeCode];

  return (
    <AppShell kind="parent" title="语文听写">
      <div className="space-y-4">
        {/* ---------- 第一屏：这是给谁的听写 ---------- */}
        <Card className="bg-brand-50/60">
          <h2 className="text-base font-semibold text-slate-900">
            {student.nickname} 的语文听写
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            这里的每一次听写都只属于 {student.nickname}：词从全家的字表里取，
            但记录、节奏与录音都是他自己的。
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge>{gradeLabel(student.gradeCode)}</Badge>
            <Badge tone={canEdit ? 'success' : 'warn'}>
              {canEdit ? '你可以布置听写' : '你没有编辑权限（只能查看）'}
            </Badge>
            {tempoSuggestion !== undefined && (
              <Badge tone="neutral">
                {tempoSuggestion.source === 'last_session' ? '节奏沿用上次' : '节奏用默认值'}
              </Badge>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            字表（课本、课文、生字、词语）是全家共用的，在「工具 → 语文听写」里录一次，{' '}
            {student.nickname} 和别的孩子都用同一份。
          </p>
        </Card>

        {/* ---------- Tab ---------- */}
        <div role="tablist" aria-label="语文听写" className="flex flex-wrap gap-2">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
              className={
                tab === item.key
                  ? 'rounded-xl bg-brand-600 px-3 py-2 text-left text-sm font-semibold text-white'
                  : 'rounded-xl border border-slate-300 bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50'
              }
            >
              <span className="block">{item.label}</span>
              <span
                className={
                  tab === item.key
                    ? 'block text-[11px] text-brand-100'
                    : 'block text-[11px] text-slate-400'
                }
              >
                {item.hint}
              </span>
            </button>
          ))}
        </div>

        {/* ============================================ 批量任务（迁移 0020） */}
        {tab === 'assignments' && (
          <DictationAssignmentsPanel
            studentId={id}
            studentNickname={student.nickname}
            defaultGradeCode={student.gradeCode}
            canEdit={canEdit}
            active={tab === 'assignments'}
            /*
              2026-10-29：任务列表搬到「听写历史」了，布置完直接切过去。
              ⚠️ **不展开**（默认折叠，与桌面端一致）；组头本身会显示任务名与 `0 批`。
            */
            onCreated={() => setTab('history')}
          />
        )}

        {/* ============================================ 布置听写 */}
        {tab === 'build' && (
          <>
            <Card>
              <CardHeader
                title="选要听写的词"
                description="这些条件交给后端取词，取到什么就出什么题 —— 界面上只会看到实际取到的结果。"
              />

              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-slate-500">年级（可多选）</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {GRADES.map((grade) => {
                      const selected = draft.gradeCodes.includes(grade.code);
                      return (
                        <button
                          key={grade.code}
                          type="button"
                          aria-pressed={selected}
                          onClick={() =>
                            setDraft((prev) => ({
                              ...prev,
                              gradeCodes: selected
                                ? prev.gradeCodes.filter((code) => code !== grade.code)
                                : [...prev.gradeCodes, grade.code],
                            }))
                          }
                          className={
                            selected
                              ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                              : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                          }
                        >
                          {gradeLabel(grade.code)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">
                    {draft.gradeCodes.length === 0
                      ? '一个都没选 —— 会取全部年级的词。'
                      : `默认选中${gradeLabel(student.gradeCode)}（这个孩子的年级），可以随时改。`}
                  </p>
                </div>

                <div className="flex flex-wrap gap-6">
                  <div>
                    <p className="text-xs font-medium text-slate-500">学期</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {['上', '下', '不分'].map((semester) => {
                        const selected = draft.semesters.includes(semester);
                        return (
                          <button
                            key={semester}
                            type="button"
                            aria-pressed={selected}
                            onClick={() =>
                              setDraft((prev) => ({
                                ...prev,
                                semesters: selected
                                  ? prev.semesters.filter((item) => item !== semester)
                                  : [...prev.semesters, semester],
                              }))
                            }
                            className={
                              selected
                                ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                                : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                            }
                          >
                            {semesterLabel(semester)}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">全不选 = 上下册都取。</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500">类别</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {[
                        { value: 'common', label: '常用词' },
                        { value: 'phrase', label: '词语' },
                        { value: 'idiom', label: '成语' },
                      ].map((option) => {
                        const selected = draft.categories.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() =>
                              setDraft((prev) => ({
                                ...prev,
                                categories: selected
                                  ? prev.categories.filter((item) => item !== option.value)
                                  : [...prev.categories, option.value],
                              }))
                            }
                            className={
                              selected
                                ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                                : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                            }
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-400">全不选 = 所有类别。</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex items-end gap-2 pb-2 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300"
                      checked={draft.fromTextbookOnly}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, fromTextbookOnly: event.target.checked }))
                      }
                    />
                    只看课本词语表
                  </label>
                  <div>
                    <label
                      className="block text-sm font-medium text-slate-700"
                      htmlFor="dictation-random-count"
                    >
                      {draft.source === 'all' ? '随机取几个（留空 = 按顺序全取）' : '最多取几个'}
                    </label>
                    <input
                      id="dictation-random-count"
                      className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      inputMode="numeric"
                      value={draft.randomCount}
                      placeholder="例如 20"
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, randomCount: event.target.value }))
                      }
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      {draft.source === 'all'
                        ? '最多 200。留空就是「按文字顺序全取」（最多 200 个）。'
                        : '最多 200。错词的顺序是「最该复习的排最前」，所以这里只当上限用，不做随机。'}
                    </p>
                  </div>
                </div>

                {/*
                  取词来源（第四步）。默认「全部词语」；选错词的两个选项时，
                  下面会说明**排序规则** —— 家长必须知道那份名单是按什么排的，
                  否则"今天这几个词为什么是这几个"永远说不清。
                */}
                <div>
                  <p className="text-xs font-medium text-slate-500">取词来源</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(
                      [
                        { value: 'all', label: '全部词语' },
                        { value: 'wrong_first', label: '错词优先' },
                        { value: 'wrong', label: '只听写错词' },
                      ] as const
                    ).map((option) => {
                      const selected = draft.source === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => setDraft((prev) => ({ ...prev, source: option.value }))}
                          className={
                            selected
                              ? 'rounded-lg border border-brand-400 bg-brand-50 px-2 py-1 text-sm text-brand-700'
                              : 'rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 hover:bg-slate-50'
                          }
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-[11px] text-slate-400">
                    {draft.source === 'all'
                      ? '按文字顺序取（填了「随机取几个」则随机）。'
                      : draft.source === 'wrong_first'
                        ? '先把错词本里还没掌握的词按「下次该复习的时间」从早到晚排前面，剩余名额补这个范围内的新词（按文字顺序）。'
                        : '词全部来自错词本，按「下次该复习的时间」从早到晚排 —— 一个都不补。'}
                  </p>
                </div>
              </div>
            </Card>

            <Card>
              <CardHeader
                title="听写节奏"
                description="这三项决定孩子来不来得及写。改一个数，下面那句效果说明与总时长会立刻跟着变。"
              />
              <TempoForm
                tempo={effectiveTempo}
                wordCount={parseRandomCount(draft.randomCount)}
                disabled={!canEdit || createMutation.isPending}
                onChange={(patch) =>
                  setDraft((prev) => ({
                    ...prev,
                    tempoTouched: true,
                    tempo: { ...prev.tempo, ...patch },
                  }))
                }
              />
              <div className="mt-3">
                <label
                  className="block text-sm font-medium text-slate-700"
                  htmlFor="dictation-session-title"
                >
                  这次听写的名字（留空按今天日期自动起）
                </label>
                <input
                  id="dictation-session-title"
                  className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                  value={draft.title}
                  maxLength={64}
                  placeholder="例如：10月15日 语文听写"
                  onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
                />
              </div>

              {createError !== null && (
                <p
                  role="alert"
                  className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                >
                  <span className="mt-0.5 shrink-0">
                    <IconAlertTriangle size={16} />
                  </span>
                  <span>{createError}</span>
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  loading={createMutation.isPending}
                  disabled={!canEdit}
                  icon={<IconPlus size={18} />}
                  onClick={() => {
                    setCreateError(null);
                    createMutation.mutate();
                  }}
                >
                  布置这次听写
                </Button>
                <span className="text-xs text-slate-500">
                  布置之后到「听写历史」里点「开始听写」—— 播放器要你按一下才会出声。
                </span>
              </div>
              {!canEdit && (
                <p className="mt-2 text-sm text-amber-700">
                  你是「仅查看」权限，看得到但不能布置听写。
                </p>
              )}
            </Card>

            <Card className="bg-slate-50">
              <CardHeader
                title="想按错词听写？"
                description="上面「取词来源」里选「错词优先」或「只听写错词」就行 —— 错词来自家长批改时标出的那几个。"
              />
              <p className="text-sm text-slate-500">
                错词在批改后自动进错词本，并按 1 → 3 → 7 天的阶梯排下次复习。
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to={`/students/${id}/dictation/mistakes`}>
                  <Button size="sm" variant="secondary">
                    <span className="mr-1 inline-flex">
                      <IconHistory size={16} />
                    </span>
                    打开错词本
                  </Button>
                </Link>
              </div>
            </Card>
          </>
        )}

        {/* ============================================ 听写历史 */}
        {tab === 'history' && (
          <>
            {(historyQuery.isError || sessionsMissing) && (
              <ErrorState
                message={
                  historyQuery.isError
                    ? `没拿到听写历史：${toErrorMessage(historyQuery.error)}`
                    : '没拿到听写历史（后端返回的字段名与预期不一致）—— 这不是"一次都没听过"。'
                }
                onRetry={() => void historyQuery.refetch()}
                compact
              />
            )}

            <Card>
              <CardHeader
                title="听写历史"
                description="按**任务**分组：每一批都挂在它所属的那个批量任务下面；不属于任何任务的（「布置听写」出的那一次）单独一组。"
                actionAlign="start"
                action={
                  <div className="flex basis-full flex-wrap items-center gap-2">
                    <Badge tone={historyQuery.data?.total === undefined ? 'warn' : 'neutral'}>
                      共 {countLabel(historyQuery.data?.total ?? null)} 次
                    </Badge>
                    <select
                      aria-label="筛选状态"
                      className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm"
                      value={historyStatus}
                      onChange={(event) => setHistoryStatus(event.target.value)}
                    >
                      <option value="">全部状态</option>
                      <option value="pending">待开始</option>
                      <option value="in_progress">进行中</option>
                      <option value="completed">已完成</option>
                      {/*
                        第四步：`graded` 现在是真实存在的状态（提交批改后会变成它），
                        所以它必须能筛 —— 少了它，家长找不到"哪些还没批改"。
                      */}
                      <option value="graded">已批改</option>
                    </select>
                    <Link to={`/students/${id}/dictation/mistakes`}>
                      <Button size="sm" variant="secondary">
                        <span className="mr-1 inline-flex">
                          <IconHistory size={16} />
                        </span>
                        错词本
                      </Button>
                    </Link>
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<IconPlus size={16} />}
                      onClick={() => setTab('build')}
                    >
                      布置新的
                    </Button>
                  </div>
                }
              />

              {historyQuery.isLoading || historyAssignmentsQuery.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : sessions !== null && sessions.length === 0 ? (
                <EmptyState
                  icon="📝"
                  title="还没有布置过听写"
                  description="先在「批量任务」里立一个任务，或者用「布置听写」出一次；听过的记录都会回到这里。"
                  action={
                    <Button size="sm" className="mt-2" onClick={() => setTab('build')}>
                      去布置
                    </Button>
                  }
                />
              ) : (
                /*
                  按**任务**分组（2026-10-29 用户要求）：
                    "每一批听写的历史，都挂在这个总的听写任务下面。"
                  分组规则是纯函数 `groupHistoryByAssignment()`（有 10 条用例），
                  这里只负责画。
                */
                <div className="space-y-3">
                  {historyGroups.map((group) => {
                    const progressText = describeGroupProgress(group);
                    const expanded = expandedGroups.has(group.key);
                    const batchCount = group.sessions.length;
                    return (
                      <section key={group.key} className="min-w-0">
                        {/*
                          ---------- 组头：点标题展开/折叠 ----------

                          ⚠️ 2026-10-29 修（手机截图报的"听写历史里没有听写任务，只有第几批"）：
                              原来标题与管理按钮是**同一行**的左右两半 —— 标题
                              `min-w-0 flex-1` 会被右侧「进行中 / 暂停 / 结束 / 删除」
                              那一排 `shrink-0` 的按钮压到 **0 宽**（标题用 `truncate` 于是彻底看不见），
                              手机上就只剩一串「第 N 批」。
                              现在改成**上下两层**：标题独占一行（`w-full`），
                              状态与按钮换到下一行 `flex-wrap`。
                              凡是"标题 + 一排按钮"的横排，都别让标题成为唯一可压缩的那一半。
                        */}
                        <div className="rounded-xl bg-slate-50 px-3 py-2">
                          {/*
                            ⚠️ 折叠开关是一个 <button>，而管理按钮在它**外面**
                            （button 不能嵌 button，否则点击会冒泡成两次动作）。
                          */}
                          <button
                            type="button"
                            aria-expanded={expanded}
                            onClick={() => toggleGroup(group.key)}
                            className="flex w-full min-w-0 items-start gap-2 text-left"
                          >
                            <span
                              aria-hidden="true"
                              className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${
                                expanded ? 'rotate-90' : ''
                              }`}
                            >
                              ▶
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block break-words text-sm font-semibold text-slate-800">
                                {group.title}
                              </span>
                              <span className="block text-xs text-slate-500">
                                {batchCount === 0 ? '还没有听过任何一批' : `${batchCount} 批`}
                                {progressText === null ? '' : ` · ${progressText}`}
                              </span>
                            </span>
                          </button>

                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {group.assignment !== null ? (
                              <>
                                <Badge
                                  tone={
                                    group.assignment.status === 'active'
                                      ? 'brand'
                                      : group.assignment.status === 'paused'
                                        ? 'warn'
                                        : 'neutral'
                                  }
                                >
                                  {assignmentApi.assignmentStatusLabel(group.assignment.status)}
                                </Badge>
                                {/*
                                  暂停 / 恢复 / 结束 / 删除 —— 从「批量任务」页签搬到这里
                                  （用户："没必要在两个地方都出现同一个东西"）。
                                */}
                                <AssignmentAdminActions
                                  studentId={id}
                                  assignment={group.assignment}
                                  canEdit={canEdit}
                                />
                              </>
                            ) : (
                              <Badge tone="neutral">不属于批量任务</Badge>
                            )}
                          </div>
                        </div>

                        {/* ---------- 组内：一批一批（默认折叠） ---------- */}
                        {expanded && (
                          <ul className="mt-2 space-y-2">
                            {group.sessions.length === 0 ? (
                              <li className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-500">
                                这个任务还没有听写记录 —— 孩子点「开始听写」之后，第 1 批会出现在这里。
                              </li>
                            ) : (
                              group.sessions.map((session) => (
                            <li
                              key={session.id}
                              className="min-w-0 rounded-xl border border-slate-200 px-3 py-3"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="truncate font-semibold text-slate-900">
                                    {/*
                                      属于任务时只显示「第 N 批」（任务名已经在组头上了，
                                      再重复一遍会让每一行都很长）；独立听写显示它自己的标题。
                                    */}
                                    {session.assignmentId === null
                                      ? session.title
                                      : batchLabel(session)}
                                  </p>
                                  <p className="mt-0.5 truncate text-xs text-slate-500">
                                    {session.questionCount} 个词 · 每个词读{' '}
                                    {session.tempo.repeatCount} 遍 · 间隔 {session.tempo.gapSeconds}{' '}
                                    秒 · 换词再停 {session.tempo.betweenWordsSeconds} 秒
                                  </p>
                                  <p className="mt-0.5 truncate text-[11px] text-slate-400">
                                    {describeAppliedCriteria(session.pickCriteria)}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  <Badge
                                    tone={
                                      session.status === 'graded'
                                        ? 'success'
                                        : session.status === 'completed'
                                          ? 'brand'
                                          : 'neutral'
                                    }
                                  >
                                    {sessionApi.sessionStatusLabel(session.status)}
                                  </Badge>
                                </div>
                              </div>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <Link to={`/students/${id}/dictation/${session.id}`}>
                                  <Button size="sm">
                                    {session.status === 'pending' ? '开始听写' : '再听一遍'}
                                  </Button>
                                </Link>
                                {/*
                                  批改与看题目**是同一个页面**，所以只留一个按钮
                                  （2026-10-29 用户原话："这两个按钮点进去连去了同一个界面，
                                  建议把「去批改」和「看题目」合并，去掉「去批改」按钮"）。
                                  文案按状态变：
                                    · 孩子没交（pending / in_progress）→ 「看题目」（只读，
                                      后端也会拒批改 `DICTATION_NOT_GRADEABLE`）；
                                    · 交了没批 → 「去批改」（这一步是他要做的事）；
                                    · 批过 → 「改批改」（可重复修改）。
                                */}
                                <Link to={`/students/${id}/dictation/${session.id}/review`}>
                                  <Button
                                    size="sm"
                                    variant={session.status === 'completed' ? 'primary' : 'secondary'}
                                  >
                                    {session.status === 'completed'
                                      ? '去批改'
                                      : session.status === 'graded'
                                        ? '改批改'
                                        : '看题目'}
                                  </Button>
                                </Link>
                                {/*
                                  2026-10-29 用户要求：**批量任务的批**后面只留两个按钮
                                  （「照样再来一次」与「删除」都去掉：前者只是把条件填回
                                  「布置听写」；后者会把这次听写的题目一起删掉，而任务级的
                                  删除在组头上，那才是他真正想做的"删掉这份计划"）。

                                  ⚠️ **独立听写**（`assignmentId === null`）保留「删除」：
                                      它没有任务组头可以删，不在这里删就再也没有入口了
                                      —— 2026-10-29 用户看到独立听写后明确要求补回来。
                                */}
                                {session.assignmentId === null && canEdit && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    aria-label={`删除 ${session.title}`}
                                    title="删除这次听写"
                                    icon={<IconTrash size={16} />}
                                    loading={deleteSessionMutation.isPending}
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `确定删除「${session.title}」吗？题目会一起删掉，删了不能恢复。`,
                                        )
                                      ) {
                                        deleteSessionMutation.mutate(session.id);
                                      }
                                    }}
                                  >
                                    删除
                                  </Button>
                                )}
                              </div>
                            </li>
                              ))
                            )}
                          </ul>
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </Card>
          </>
        )}

        {/* ============================================ 朗读录音 */}
        {tab === 'audios' && (
          <DictationRecorderPanel
            studentId={id}
            studentNickname={student.nickname}
            canEdit={canEdit}
            focusWordId={focusWordId}
            focusWordText={focusWordText}
            audios={audiosQuery.data?.audios ?? null}
            audiosMissing={audiosQuery.data !== undefined && audiosQuery.data.audios === null}
            total={audiosQuery.data?.total ?? null}
            loading={audiosQuery.isLoading}
            error={audiosQuery.isError ? toErrorMessage(audiosQuery.error) : null}
            onRetry={() => void audiosQuery.refetch()}
            onChanged={() => {
              void qc.invalidateQueries({ queryKey: sKeys.audios(id) });
            }}
          />
        )}

        {/* ---------- 底部提示：字表入口 ---------- */}
        <Card className="bg-slate-50">
          <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <IconBookOpen size={18} />
            字表里还没有要听写的词？
            <Link
              to={`/tools/dictation?anchor=${id}`}
              className="font-medium text-brand-700 hover:underline"
            >
              去「工具 → 语文听写」录入或粘贴导入
            </Link>
            <IconHistory size={18} />
            <span className="flex items-center gap-1">
              <IconMic size={16} />
              录音只在你这台设备上录，存在服务器里，家里别的手机也能放。
            </span>
          </p>
        </Card>
      </div>
    </AppShell>
  );
}
